import {
  QueryClient,
  QueryFunctionContext,
  useInfiniteQuery,
} from "@tanstack/react-query";

const VOCABULARY_LIST_QUERY_KEY = "vocabulary-list-query";

export function invalidateQuery(client: QueryClient): void {
  client.invalidateQueries({ queryKey: [VOCABULARY_LIST_QUERY_KEY] });
}

export interface VocabularyDto {
  id: number;
  created_at?: string;
  word: string;
  pattern_unlock_ids?: string[];
  word_language: string;
  pronunciation_status?: "NOT_REQUESTED" | "PENDING" | "COMPLETE" | "FAILED";
  pronunciation_audio?: string | null;
  translation_language?: string;
  translation: string;
  translation_details?: {
    translation_word?: string;
    source_surface_form?: string | null;
    source_dictionary_form?: string | null;
    source_lemma?: string | null;
    source_article?: string | null;
    source_plural_form?: string | null;
    source_context_hint?: string | null;
    grammar?: {
      part_of_speech?: string | null;
      tense?: string | null;
      mood?: string | null;
      person?: string | null;
      number?: string | null;
      gender?: string | null;
      case?: string | null;
      syntactic_function?: string | null;
    } | null;
    alternative_meanings?: string | null;
    translated_sentence?: string | null;
    structure?: {
      core_idea?: string | null;
      memory_hint?: string | null;
      word_meaning?: string | null;
      form_note?: string | null;
      pattern_explanation?: string | null;
      contextual_meaning?: string | null;
      etymology?: string | null;
      example_breakdown?: string[] | null;
      related_examples?: string[] | null;
      literal_meaning?: string | null;
      grammatical_form?: string | null;
      related_pattern_examples?: string[] | null;
    } | null;
  };
  winning_streak: number;
  last_tested: string | null;
  review_direction?: "WORD_TO_TRANSLATION" | "TRANSLATION_TO_WORD";
  mnemonic: string | null;
  sentence: string | null;
  status: "PENDING" | "COMPLETE" | "FAILED";
  origin?: "MANUAL" | "PHOTO_IMPORT" | string;
  photo_import_id?: number | null;
  usages: {
    text: number;
    source_title?: string;
    source_kind?: "ORF article" | "reading";
    chapter_id: number | null;
    chapter_counter?: number | null;
    start_idx: number;
    end_idx: number;
  }[];
}

interface VocabularyDtoPage {
  next: string | null;
  previous?: string | null;
  count?: number;
  results: VocabularyDto[];
}

const fetchPage = async (
  pageParam: string,
  generated_text_id?: number,
  generated_text_created_at_last_days?: number,
  languages?: string[],
  origins?: string[],
  rabbit_hole_week_id?: number,
  page_size?: number,
  signal?: AbortSignal,
) => {
  const queryParams = new URLSearchParams({ cursor: pageParam });
  if (page_size !== undefined) {
    queryParams.append("page_size", page_size.toString());
  }
  if (languages !== undefined) {
    for (const lang of languages) {
      queryParams.append("language", lang);
    }
  }
  if (generated_text_id !== undefined) {
    queryParams.append("generated_text_id", generated_text_id.toString());
  }
  if (generated_text_created_at_last_days !== undefined) {
    queryParams.append(
      "generated_text_created_at_last_days",
      generated_text_created_at_last_days.toString(),
    );
  }
  if (origins !== undefined) {
    for (const origin of origins) {
      queryParams.append("origin", origin);
    }
  }
  if (rabbit_hole_week_id !== undefined) {
    queryParams.append("rabbit_hole_week_id", rabbit_hole_week_id.toString());
  }
  const res = await fetch(
    "/api/reeprise/vocabulary/?" + queryParams.toString(),
    {
      signal,
    },
  );

  if (!res.ok) {
    throw new Error("could not fetch vocabulary");
  }
  return (await res.json()) as VocabularyDtoPage;
};

export function usePaginatedVocabularyList(params?: {
  enabled?: boolean;
  generated_text_id?: number;
  rabbit_hole_week_id?: number;
  word_languages?: string[];
  generated_text_created_at_last_days?: number;
  origins?: string[];
  page_size?: number;
}) {
  const paginatedVocabularyListQuery = useInfiniteQuery({
    queryKey: [
      VOCABULARY_LIST_QUERY_KEY,
      {
        enabled: params?.enabled ?? true,
        textId: params?.generated_text_id,
        rabbitHoleWeekId: params?.rabbit_hole_week_id,
        languages: params?.word_languages,
        origins: params?.origins,
        pageSize: params?.page_size,
        textCreatedWindow: params?.generated_text_created_at_last_days,
      },
    ],
    enabled: params?.enabled ?? true,
    queryFn: ({ pageParam, signal }: QueryFunctionContext) =>
      fetchPage(
        pageParam as string,
        params?.generated_text_id,
        params?.generated_text_created_at_last_days,
        params?.word_languages,
        params?.origins,
        params?.rabbit_hole_week_id,
        params?.page_size,
        signal,
      ),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next,
    refetchInterval: (query) => {
      if (
        query.state.data &&
        query.state.data.pages
          .flatMap((page) => page.results)
          .find(
            (voc) =>
              voc.status === "PENDING" || voc.pronunciation_status === "PENDING",
          )
      ) {
        return 500;
      }
      return false;
    },
  });

  return paginatedVocabularyListQuery;
}
